// Sai: movement physics, jumping (coyote time + buffering + double jump), sword, damage, power-ups.
import * as THREE from 'three';
import { moveBody, depenetrate, boxFree } from './physics.js';
import { B, BLOCKS } from './blocks.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { clamp, dampAngle } from './util.js';

export const GRAVITY = 30;
export const JUMP_V = 9.6;
export const DJUMP_V = 9.0;
export const WALK = 6.4;
export const AIR_ACCEL = 28;

export class Player {
  constructor(level, character) {
    this.level = level;
    this.ch = character;
    this.pos = new THREE.Vector3();
    this.vel = new THREE.Vector3();
    this.hw = 0.3;
    this.h = 1.75;
    this.onGround = false;
    this.groundId = 0;
    this.platform = null;
    this.self = null;
    this.facing = 0;
    this.coyote = 0;
    this.jumpBuf = 0;
    this.jumpsUsed = 0;
    this.jumpCut = false;
    this.swingT = -1;
    this.swingHitDone = false;
    this.invuln = 0;
    this.maxHearts = 3;
    this.hearts = 3;
    this.frozen = false;
    this.dead = false;
    this.power = { magnet: 0, speed: 0, jump: 0, shield: 0 };
    this.stepAcc = 0;
    this.airTime = 0;
    this.fallStart = 0;
    this.trailT = 0;
    this.knockT = 0;
    this.inWater = false;
    this.damageTaken = 0;
    this.lastSafe = new THREE.Vector3();
  }

  get canDoubleJump() {
    return this.level.game.save.data.unlocks.doubleJump || this.level.forceDoubleJump || this.level.game.save.data.discovery.expansion?.gear.boots;
  }

  spawn(p, yaw = 0) {
    this.pos.copy(p);
    this.vel.set(0, 0, 0);
    this.facing = yaw;
    this.ch.root.position.copy(p);
    this.ch.root.rotation.y = yaw;
    this.dead = false;
    this.invuln = 1.2;
    this.knockT = 0;
    this.jumpsUsed = 0;
  }

  knock(dir, h = 7, up = 6) {
    this.vel.x = dir.x * h;
    this.vel.z = dir.z * h;
    this.vel.y = up;
    this.knockT = 0.35;
    this.onGround = false;
  }

  hurt(n, fromPos, force = 7) {
    if (this.invuln > 0 || this.dead || this.frozen) return;
    if (this.power.shield > 0) {
      const d = new THREE.Vector3().subVectors(this.pos, fromPos).setY(0).normalize();
      this.knock(d, force * 0.6, 4);
      audio.play('hit');
      haptics.play('block');
      return;
    }
    const gear=this.level.game.save.data.discovery.expansion?.gear;
    this.armourHits=(this.armourHits||0)+1;
    if(gear?.armour && this.armourHits % (gear.armour==='armour_diamond'?2:3)===0) {this.invuln=1.3;audio.play('hit');haptics.play('block');return;}
    this.hearts -= n;
    this.damageTaken += n;
    this.invuln = 1.3;
    this.ch.hurtFlash();
    audio.play('hurt');
    haptics.play('hurt');
    const d = new THREE.Vector3().subVectors(this.pos, fromPos).setY(0);
    if (d.lengthSq() < 0.001) d.set(Math.sin(this.facing + Math.PI), 0, Math.cos(this.facing + Math.PI));
    d.normalize();
    this.knock(d, force, 6.5);
    this.level.onPlayerHurt();
    if (this.hearts <= 0) this.level.playerDied('hearts');
  }

  heal(n = 1) {
    this.hearts = Math.min(this.maxHearts, this.hearts + n);
  }

  update(dt, input, camYaw) {
    const L = this.level;
    const W = L.world;
    this.invuln = Math.max(0, this.invuln - dt);
    for (const k of Object.keys(this.power)) this.power[k] = Math.max(0, this.power[k] - dt);
    this.knockT = Math.max(0, this.knockT - dt);

    // ride moving platforms
    if (this.platform && this.platform.delta) this.pos.add(this.platform.delta);
    depenetrate(this, L.colliders);

    let mx = 0, my = 0;
    if (!this.frozen && !this.dead) {
      mx = input.moveX;
      my = input.moveY;
    }
    const fx = Math.sin(camYaw), fz = Math.cos(camYaw);
    const rx = -Math.cos(camYaw), rz = Math.sin(camYaw);
    let wx = fx * my + rx * mx, wz = fz * my + rz * mx;
    const wl = Math.hypot(wx, wz);
    if (wl > 1) {
      wx /= wl;
      wz /= wl;
    }
    const block = BLOCKS[this.groundId] || BLOCKS[0];
    const slippery = this.onGround && block.slippery;
    const speedMul = this.power.speed > 0 ? 1.55 : 1;
    const max = WALK * speedMul * (this.inWater ? 0.6 : 1);
    const tx = wx * max, tz = wz * max;
    let accel = this.onGround ? (slippery ? 7 : 65) : AIR_ACCEL;
    if (this.knockT > 0) accel = 4;
    const ax = clamp(tx - this.vel.x, -accel * dt, accel * dt);
    const az = clamp(tz - this.vel.z, -accel * dt, accel * dt);
    this.vel.x += ax;
    this.vel.z += az;
    if (wl > 0.1 && this.knockT <= 0) this.facing = dampAngle(this.facing, Math.atan2(wx, wz), 14, dt);

    // jumping
    if (input.jumpPressed && !this.frozen && !this.dead) this.jumpBuf = 0.15;
    else this.jumpBuf -= dt;
    this.coyote = this.onGround ? 0.12 : this.coyote - dt;
    const jumpBoost = this.power.jump > 0 ? 1.3 : 1;
    if (this.jumpBuf > 0) {
      if (this.coyote > 0) {
        this.vel.y = JUMP_V * jumpBoost;
        this.jumpBuf = 0;
        this.coyote = 0;
        this.jumpsUsed = 1;
        this.jumpCut = false;
        this.onGround = false;
        audio.play('jump');
        haptics.play('jump');
        L.stats.jumps++;
        L.fx.particles.dust(this.pos, 4, block.color);
      } else if (this.inWater) {
        this.vel.y = 6;
        this.jumpBuf = 0;
      } else if (this.canDoubleJump && this.jumpsUsed < 2) {
        this.vel.y = DJUMP_V * jumpBoost;
        this.jumpBuf = 0;
        this.jumpsUsed = 2;
        this.jumpCut = false;
        audio.play('doubleJump');
        haptics.play('doubleJump');
        L.fx.rings.add(this.pos.clone(), { color: '#bfe8ff', from: 0.3, to: 1.6, life: 0.35 });
        L.fx.particles.burst(this.pos, { count: 10, colors: ['#ffffff', '#bfe8ff'], speed: 3, up: -1, size: 0.1, glow: true, gravity: 2 });
      }
    }
    // variable jump height: let go early for a short hop
    if (!input.jumpHeld && this.vel.y > 4 && !this.jumpCut && this.jumpsUsed > 0 && this.knockT <= 0 && !this.bounced) {
      this.vel.y *= 0.55;
      this.jumpCut = true;
    }

    // auto-jump up single blocks (like Minecraft's auto-jump)
    if (L.game.save.data.settings.autoJump && this.onGround && wl > 0.5 && !this.frozen) {
      const ax2 = this.pos.x + (wx / (wl || 1)) * (this.hw + 0.35);
      const az2 = this.pos.z + (wz / (wl || 1)) * (this.hw + 0.35);
      const fy = Math.floor(this.pos.y + 0.05);
      const front = W.get(Math.floor(ax2), fy, Math.floor(az2));
      if (front && BLOCKS[front].solid && !BLOCKS[front].hurt) {
        if (boxFree(W, ax2 - 0.3, fy + 1, az2 - 0.3, ax2 + 0.3, fy + 2.8, az2 + 0.3) && boxFree(W, this.pos.x - 0.3, fy + 1, this.pos.z - 0.3, this.pos.x + 0.3, fy + 2.8, this.pos.z + 0.3)) {
          this.vel.y = 8.2;
          this.onGround = false;
          this.coyote = 0;
          this.jumpsUsed = 1;
          this.jumpCut = true;
        }
      }
    }

    // gravity & water
    const feet = W.get(Math.floor(this.pos.x), Math.floor(this.pos.y + 0.2), Math.floor(this.pos.z));
    const wasWater = this.inWater;
    this.inWater = BLOCKS[feet]?.liquid === 'water';
    if (this.inWater && !wasWater && this.vel.y < -6) {
      audio.play('splash');
      L.fx.particles.burst(this.pos.clone().add(new THREE.Vector3(0, 0.5, 0)), { count: 16, colors: ['#9fd4ff', '#ffffff'], speed: 3, up: 6, size: 0.12 });
    }
    if (this.inWater) {
      this.vel.y = Math.max(this.vel.y - 9 * dt, -3);
      this.jumpsUsed = 0;
    } else this.vel.y = Math.max(-38, this.vel.y - GRAVITY * dt);

    const ladder = [0,.8].some(dy=>BLOCKS[W.getF(this.pos.x,this.pos.y+dy,this.pos.z)]?.climb);
    if(ladder&&!this.frozen){this.vel.y=input.jumpHeld||input.moveY>.2?4:input.moveY<-.2?-3:0;this.jumpsUsed=0;}
    const wasGround = this.onGround;
    const vyBefore = this.vel.y;
    moveBody(this, W, L.colliders, this.vel.x * dt, this.vel.y * dt, this.vel.z * dt);

    if (this.onGround) {
      if (!wasGround) {
        // landed
        this.jumpsUsed = 0;
        this.bounced = false;
        const g = BLOCKS[this.groundId];
        if (g && g.bounce) {
          this.vel.y = g.bounce * (this.power.jump > 0 ? 1.15 : 1);
          this.onGround = false;
          this.bounced = true;
          this.jumpsUsed = 1;
          audio.play('bounce');
          haptics.play('bounce');
          L.fx.rings.add(this.pos.clone(), { color: '#9cff7a', to: 2.4, life: 0.4 });
          L.fx.particles.burst(this.pos, { count: 12, colors: ['#9cff7a', '#ffffff'], speed: 4, up: 3, size: 0.12, glow: true });
          L.stats.bounces = (L.stats.bounces || 0) + 1;
        } else if (vyBefore < -8) {
          this.ch.land(Math.min(1, -vyBefore / 20));
          audio.play('land', { pitch: Math.min(1.5, -vyBefore / 14) });
          haptics.play('land');
          L.fx.particles.dust(this.pos, 7, g?.color || '#d8cbb0');
        }
      }
      const g = BLOCKS[this.groundId];
      if (g && g.speed && Math.hypot(this.vel.x, this.vel.z) > 0.5) {
        const d = Math.hypot(this.vel.x, this.vel.z);
        this.vel.x = (this.vel.x / d) * 13;
        this.vel.z = (this.vel.z / d) * 13;
        this.knockT = 0.4;
        if (!this.speedSnd || L.time - this.speedSnd > 0.5) {
          audio.play('speed');
          haptics.play('speed');
          this.speedSnd = L.time;
        }
      }
      if (!this.platform && g && g.solid && !g.hurt && !g.bounce) this.lastSafe.copy(this.pos);
      this.airTime = 0;
    } else this.airTime += dt;

    // hazards: lava
    const touchId = W.get(Math.floor(this.pos.x), Math.floor(this.pos.y + 0.1), Math.floor(this.pos.z));
    const underId = W.get(Math.floor(this.pos.x), Math.floor(this.pos.y - 0.2), Math.floor(this.pos.z));
    if (BLOCKS[touchId]?.hurt || BLOCKS[underId]?.hurt) {
      if (!this.dead) {
        L.fx.particles.burst(this.pos, { count: 20, colors: ['#ff8a20', '#ffd040', '#3a3a3a'], speed: 4, up: 7, size: 0.14, glow: true });
        audio.play('hurt');
        L.playerDied('lava');
      }
    }
    if (this.pos.y < L.killY && !this.dead) L.playerDied('fall');

    // footsteps
    const hs = Math.hypot(this.vel.x, this.vel.z);
    if (this.onGround && hs > 1) {
      this.stepAcc += hs * dt;
      if (this.stepAcc > 2.1) {
        this.stepAcc = 0;
        const g = BLOCKS[this.groundId];
        audio.play('step_' + (g?.step || 'stone'), { pitch: 0.9 + Math.random() * 0.2 });
      }
    }

    // trail cosmetic
    const trail = L.game.save.data.equip.trail;
    const trailType = this.power.shield > 0 ? 'rainbow' : this.power.speed > 0 ? 'lightning' : trail;
    if (trailType && trailType !== 'none' && (hs > 1.5 || !this.onGround)) {
      this.trailT += dt;
      while (this.trailT > 0.03) {
        this.trailT -= 0.03;
        L.fx.particles.trail(this.pos, trailType, L.time);
      }
    }

    // sword
    if (this.swingT >= 0) {
      this.swingT += dt / 0.36;
      if (!this.swingHitDone && this.swingT > 0.33) {
        this.swingHitDone = true;
        L.swordHit(this);
      }
      if (this.swingT >= 1) this.swingT = -1;
    }

    // character
    const c = this.ch;
    c.root.position.copy(this.pos);
    c.root.rotation.y = this.facing;
    c.root.visible = this.invuln > 0 && !this.frozen ? Math.floor(this.invuln * 12) % 2 === 0 : true;
    c.update(dt, { speed: this.onGround ? hs : hs * 0.3, grounded: this.onGround || this.inWater, vy: this.vel.y, swing: this.swingT, celebrate: this.celebrate });
    // shield glow (rainbow), restored when it runs out
    if (this.power.shield > 0) {
      const h = (L.time * 2) % 1;
      c.mat.emissive.setHSL(h, 1, 0.35);
      c.mat.emissiveIntensity = 0.8;
      this.shieldGlow = true;
    } else if (this.shieldGlow) {
      this.shieldGlow = false;
      if (c.mat.emissiveMap) c.mat.emissive.set(1, 1, 1);
      else c.mat.emissive.set(0, 0, 0);
      c.mat.emissiveIntensity = 1.2;
    }
  }

  swing() {
    if (this.swingT >= 0 || this.frozen || this.dead) return false;
    this.swingT = 0;
    this.swingHitDone = false;
    audio.play('swing');
    return true;
  }
}
